import React from 'react';
import { useTranslation } from 'react-i18next';
import { UncontrolledTooltip, Row, Col } from 'reactstrap';
import moment from 'moment/min/moment-with-locales';
import { composeDisplayName } from '../../Helpers/applicantHelper';
import { basicDateFormat, basicDateTimeFormat } from '../../Helpers/dateTimeHelper';

const HistoryItemTimelineCard = ({ historyItem, index, isDateDifferentForEvents }) => {
  const { t } = useTranslation();

  const composeTimelineIcon = () => {
    const historyItemEvent = historyItem.event;

    switch (historyItemEvent) {
      case 'EmailSent':
        return (
          <div className="timeline-badge purple" id={`timelineIconEmailSent-${index}`}>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconEmailSent-${index}`}
            >
              {t('applicants.EMAIL_SENT')}
            </UncontrolledTooltip>
          </div>
        );
      case 'ApplicationStatusChanged':
        return (
          <div className="timeline-badge warning" id={`timelineIconApplicationStatus-${index}`}>
            <em className="fa fa-shuffle"></em>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconApplicationStatus-${index}`}
            >
              {t('applicants.APPLICATION_STATUS_CHANGED')}
            </UncontrolledTooltip>
          </div>
        );
      case 'ApplicantStatusChanged':
        return (
          <div className="timeline-badge info" id={`timelineIconApplicantStatus-${index}`}>
            <em className="fa fa-repeat"></em>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconApplicantStatus-${index}`}
            >
              {t('applicants.APPLICANT_STATUS_CHANGED')}
            </UncontrolledTooltip>
          </div>
        );
      case 'ApplicantCreated':
        return (
          <div className="timeline-badge info" id={`timelineIconApplicantCreated-${index}`}>
            <em className="fa fa-user-plus"></em>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconApplicantCreated-${index}`}
            >
              {t('applicants.APPLICANT_CREATED')}
            </UncontrolledTooltip>
          </div>
        );
      case 'ApplicationCreated':
        return (
          <div className="timeline-badge info" id={`timelineIconApplicationCreated-${index}`}>
            <em className="fa-solid fa-file-circle-plus"></em>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconApplicationCreated-${index}`}
            >
              {t('applicants.APPLICATION_CREATED')}
            </UncontrolledTooltip>
          </div>
        );
      case 'ApplicationUpdated':
        return (
          <div className="timeline-badge success" id={`timelineIconApplicationUpdated-${index}`}>
            <em className="fa-solid fa-file-import"></em>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconApplicationUpdated-${index}`}
            >
              {t('applicants.APPLICATION_UPDATED')}
            </UncontrolledTooltip>
          </div>
        );
      default:
        return (
          <div className="timeline-badge success" id={`timelineIconComment-${index}`}>
            <em className="fa fa-comment"></em>
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`timelineIconComment-${index}`}
            >
              {t('applicants.COMMENT')}
            </UncontrolledTooltip>
          </div>
        );
    }
  };

  const composeCardIcon = () => {
    const historyItemEvent = historyItem.event;
    switch (historyItemEvent) {
      case 'EmailSent':
        return (
          <>
            <span id={`iconEmailSent-${index}`} />
            <UncontrolledTooltip delay={{ show: 200, hide: 0 }} placement="left" target={`iconEmailSent-${index}`}>
              {t('applicants.EMAIL_SENT')}
            </UncontrolledTooltip>
          </>
        );
      case 'ApplicationStatusChanged':
        return (
          <>
            <span id={`iconApplicationStatusChanged-${index}`} />
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`iconApplicationStatusChanged-${index}`}
            >
              {t('applicants.APPLICATION_STATUS_CHANGED')}
            </UncontrolledTooltip>
          </>
        );
      case 'ApplicantStatusChanged':
        return (
          <>
            <span id={`iconApplicantStatusChanged-${index}`} />
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`iconApplicantStatusChanged-${index}`}
            >
              {t('applicants.APPLICANT_STATUS_CHANGED')}
            </UncontrolledTooltip>
          </>
        );
      case 'ApplicantCreated':
        return (
          <>
            <span id={`iconApplicantCreated-${index}`} />
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`iconApplicantCreated-${index}`}
            >
              {t('applicants.APPLICANT_CREATED')}
            </UncontrolledTooltip>
          </>
        );
      case 'ApplicationCreated':
        return (
          <>
            <span id={`iconApplicationCreated-${index}`} />
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`iconApplicationCreated-${index}`}
            >
              {t('applicants.APPLICATION_CREATED')}
            </UncontrolledTooltip>
          </>
        );
      case 'ApplicationUpdated':
        return (
          <>
            <span id={`iconApplicationUpdated-${index}`} />
            <UncontrolledTooltip
              delay={{ show: 200, hide: 0 }}
              placement="left"
              target={`iconApplicationUpdated-${index}`}
            >
              {t('applicants.APPLICATION_UPDATED')}
            </UncontrolledTooltip>
          </>
        );
      default:
        return (
          <>
            <em className="fa mr-1 fa-comment" id={`iconComment-${index}`} />
            <UncontrolledTooltip delay={{ show: 200, hide: 0 }} placement="left" target={`iconComment-${index}`}>
              {t('applicants.COMMENT')}
            </UncontrolledTooltip>
          </>
        );
    }
  };

  const composeTitle = () => {
    // In case of email sent, oldValue is used for Subject and new value for content
    // In case of applicant created, oldValue for whole name of the applicant
    // In case of application created, oldValue for whole name of the "application name"

    const historyItemEvent = historyItem.event;

    switch (historyItemEvent) {
      case 'EmailSent':
        return (
          <>
            {composeCardIcon()}
            {t('applicants.EMAIL_SENT')}
          </>
        );
      case 'ApplicantCreated':
        return (
          <>
            {composeCardIcon()}
            {t('applicants.APPLICANT_CREATED')}
          </>
        );
      case 'ApplicationCreated':
        return (
          <>
            {composeCardIcon()}
            {t('applicants.APPLICATION_CREATED')}
          </>
        );
      case 'ApplicationUpdated':
        return (
          <>
            {composeCardIcon()}
            {t('applicants.APPLICATION_UPDATED')}
          </>
        );
      case 'ApplicationStatusChanged':
        return (
          <>
            {composeCardIcon()}
            {t('applicants.APPLICATION_STATUS_CHANGED')}
          </>
        );
      case 'ApplicantStatusChanged':
        return (
          <>
            {composeCardIcon()}
            {t('applicants.APPLICANT_STATUS_CHANGED')}
          </>
        );
      default:
        return (
          <>
            {composeCardIcon()}
            {historyItem.oldValue} {'->'} {historyItem.newValue}
          </>
        );
    }
  };

  const composeCreatorsName = () => {
    const historyItemEvent = historyItem.event;

    switch (historyItemEvent) {
      case 'ApplicantCreated':
      case 'ApplicationCreated':
        return <>System</>;
      default:
        return <>{composeDisplayName(historyItem)}</>;
    }
  };

  const composeBody = () => {
    // In case of email sent, oldValue is used for Subject and new value for content
    const historyItemEvent = historyItem.event;

    switch (historyItemEvent) {
      case 'EmailSent':
        return (
          <>
            <div className="h4 m-2">{historyItem.oldValue}</div>
            <div
              className="m-2"
              dangerouslySetInnerHTML={{
                __html: historyItem.newValue,
              }}
            />
          </>
        );
      case 'ApplicantCreated':
        return <div className="h4 popover-body">{historyItem.oldValue}</div>;
      case 'ApplicationCreated':
      case 'ApplicationUpdated':
        return (
          <div className="h4 popover-body">
            <small>{t('jobs.FOR_JOB')}:</small> {historyItem.oldValue}
          </div>
        );
      case 'ApplicationStatusChanged':
        return (
          <div className="h4 popover-body">
            {historyItem.oldValue} -{'>'} {historyItem.newValue}
          </div>
        );
      case 'ApplicantStatusChanged':
        return (
          <div className="h4 popover-body">
            {historyItem.oldValue} -{'>'} {historyItem.newValue}
          </div>
        );
      default:
        return (
          <div className="popover-body">
            <p>{historyItem.event}</p>
          </div>
        );
    }
  };

  return (
    <>
      {isDateDifferentForEvents(historyItem) === true ? (
        <li className="timeline-separator" data-datetime={moment.utc(historyItem.created).format(basicDateFormat)} />
      ) : null}
      <li className={index % 2 === 1 ? 'timeline-inverted' : null}>
        {composeTimelineIcon()}
        <div className="timeline-card">
          <div className="popover right">
            <h4 className="popover-header">
              <Row>
                <Col className="d-flex justify-content-between align-items-center">
                  <div>
                    {composeTitle()}
                    <span className="text-sm text-muted"> - {composeCreatorsName()}</span>
                  </div>
                  <span className="text-sm text-muted">
                    <i className="fa-regular fa-clock" /> &nbsp;
                    {moment.utc(historyItem.created).format(basicDateTimeFormat)}
                  </span>
                </Col>
              </Row>
            </h4>
            {composeBody()}
          </div>
        </div>
      </li>
    </>
  );
};

export default HistoryItemTimelineCard;
